import { parseQuickInput } from '@nedelnik/shared';
import { useState } from 'react';

import { haptic } from '../haptic.js';
import { Plus } from '../icons.js';

import type { CreateOutcome } from '../../core/actions.js';

/**
 * Быстрая запись — первое, что есть на экране, без перехода и раскрытия
 * (3.3.1). Задача заводится из одного заголовка (3.3.2), `#тег` разбирается
 * прямо из текста (3.3.3), поле очищается и остаётся готовым к следующей
 * записи (3.3.6).
 *
 * Кнопка рядом с полем видимая и обязательная: полагаться на один Enter нельзя
 * — на мобильной клавиатуре он до поля доходит не всегда (3.3.4).
 */
export function QuickAdd({
  onCreate,
  placeholder,
}: {
  onCreate: (raw: string) => Promise<CreateOutcome>;
  placeholder: string;
}): React.ReactElement {
  const [text, setText] = useState('');
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    if (busy) return;

    // Отклик решается до `await`: после него жест человека для iOS уже
    // закончился, и щелчка не будет. Поэтому судим по тексту, а не по итогу
    // записи: строка с заголовком почти всегда становится задачей, а без
    // заголовка — никогда, и на ней щёлкать нельзя (см. подсказку ниже).
    if (parseQuickInput(text).title.length > 0) haptic();

    setBusy(true);
    try {
      const outcome = await onCreate(text);

      // Поле чистится только при удаче: стереть набранное там, где задача не
      // завелась, — значит потерять запись и не сказать об этом.
      if (outcome === 'created') {
        setText('');
        setNotice(undefined);
      } else if (outcome === 'no-number') {
        setNotice('Номера задач ещё не приехали с сервера — нужна сеть');
      } else if (text.trim().length > 0) {
        // Строка из одних тегов даёт пустой заголовок. Промолчать здесь значит
        // сделать вид, что нажатия не было: набранное на месте, задачи нет,
        // и объяснения тоже нет.
        setNotice('Задаче нужен заголовок — одного тега мало');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="quick">
      <form
        className="quick__form"
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <input
          className="quick__input"
          aria-label="Новая задача"
          placeholder={placeholder}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <button type="submit" className="quick__go" aria-label="Добавить задачу" disabled={busy}>
          {/* Плюс на кнопке, а не стрелка: кнопка добавляет, и значок обязан
              говорить то же самое. Плюс слева от поля при этом убран — два
              плюса в одной строке читались как две разные кнопки (решение
              владельца, 2026-09-08). */}
          <Plus />
        </button>
      </form>
      {notice !== undefined && <p className="quick__notice">{notice}</p>}
    </div>
  );
}
